import { useToast } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import DefaultModal from '../../../../../../components/DefaultModal'
import SegmentTab, {
  type SegmentTabItemProps,
} from '../../../../../../components/SegmentTab'
import { useUpdateBankAccountMutation } from '../../../../../../redux/services/coreApi'
import type {
  BankAccountDetailsType,
  BankAccountDto,
} from '../../../../../../types/coreApi-types'

interface EditBankAccountModalProps {
  isOpen: boolean
  onClose: () => void
  bankAccount: BankAccountDto | null
}

export type NonNullableFields<T> = {
  [P in keyof T]: NonNullable<T[P]>
}

const EditBankAccountModal: React.FC<EditBankAccountModalProps> = ({
  isOpen,
  onClose,
  bankAccount,
}) => {
  const toast = useToast()

  const [name, setName] = useState('')
  const [description, setDescription] = useState<string | null>('')
  const [registration, setRegistration] = useState<string | null>('')
  const [accountNumber, setAccountNumber] = useState<string | null>('')
  const [iban, setIban] = useState<string | null>('')
  const [bic, setBic] = useState<string | null>('')
  const [recipientName, setRecipientName] = useState<string | null>('')
  const [recipientAddress, setRecipientAddress] = useState<string | null>('')
  const [recipientZipCode, setRecipientZipCode] = useState<string | null>('')
  const [recipientCity, setRecipientCity] = useState<string | null>('')
  const [recipientCountry, setRecipientCountry] = useState<string | null>('')
  const [code, setCode] = useState<string | null>('')
  const [bankAccountDetailsType, setBankAccountDetailsType] =
    useState<BankAccountDetailsType | null>('DomesticBankAccountDetails')

  const [updateBankAccount, { isLoading }] = useUpdateBankAccountMutation()

  useEffect(() => {
    if (bankAccount) {
      setName(bankAccount.name)
      setDescription(bankAccount.description || '')

      // Determine bank account type based on populated fields
      const details = bankAccount.bankAccountDetails
      const isDomestic = !!(details.registration || details.accountNumber)
      const detailsType = isDomestic
        ? 'DomesticBankAccountDetails'
        : 'InternationalBankAccountDetails'

      setBankAccountDetailsType(detailsType)

      // Set all fields
      setRegistration(details.registration || null)
      setAccountNumber(details.accountNumber || null)
      setIban(details.iban || null)
      setBic(details.bic || null)
      setRecipientName(details.recipientName || null)
      setRecipientAddress(details.recipientAddress || null)
      setRecipientZipCode(details.recipientZipCode || null)
      setRecipientCity(details.recipientCity || null)
      setRecipientCountry(details.recipientCountry || null)
      setCode(details.code || null)
    }
  }, [bankAccount])

  const handleSubmit = async () => {
    if (!bankAccount || !bankAccountDetailsType) return

    try {
      await updateBankAccount({
        id: bankAccount.id,
        name,
        description,
        registration,
        accountNumber,
        iban,
        bic,
        recipientName,
        recipientAddress,
        recipientZipCode,
        recipientCity,
        recipientCountry,
        code,
        bankAccountDetailsType,
      }).unwrap()

      toast({
        title: 'Success',
        description: 'Bank account updated successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })

      onClose()
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to update bank account',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  const tabs: SegmentTabItemProps[] = [
    {
      title: 'Danish',
      isActive: bankAccountDetailsType === 'DomesticBankAccountDetails',
      onClick: () => setBankAccountDetailsType('DomesticBankAccountDetails'),
    },
    {
      title: 'International',
      isActive: bankAccountDetailsType === 'InternationalBankAccountDetails',
      onClick: () =>
        setBankAccountDetailsType('InternationalBankAccountDetails'),
    },
  ]

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={isLoading}
      isLoadingText="Updating bank account..."
      title="Edit Bank Account"
      whiteBG
    >
      {bankAccount ? (
        <>
          <SegmentTab tabs={tabs} />
          <div className="p-5 pt-0 space-y-5 h-[600px]">
            <div className="max-h-[450px] overflow-y-auto">
              <p className="mb-1 text-description">Name of your bank</p>
              <input
                type="text"
                className="input mb-3"
                value={name}
                onChange={e => setName(e.target.value)}
              />
              <p className="mb-1 text-description">Description (optional)</p>
              <input
                type="text"
                className="input mb-3"
                value={description || ''}
                onChange={e => setDescription(e.target.value || null)}
              />
              {bankAccountDetailsType === 'DomesticBankAccountDetails' && (
                <>
                  <p className="mb-1 text-description">Registration number</p>
                  <input
                    type="text"
                    className="input mb-3"
                    value={registration || ''}
                    onChange={e => setRegistration(e.target.value || null)}
                  />
                  <p className="mb-1 text-description">Account number</p>
                  <input
                    type="text"
                    className="input"
                    value={accountNumber || ''}
                    onChange={e => setAccountNumber(e.target.value || null)}
                  />
                </>
              )}
              {bankAccountDetailsType === 'InternationalBankAccountDetails' && (
                <>
                  <p className="mb-1 text-description">IBAN</p>
                  <input
                    type="text"
                    className="input mb-3"
                    value={iban || ''}
                    onChange={e => setIban(e.target.value || null)}
                  />
                  <p className="mb-1 text-description">BIC</p>
                  <input
                    type="text"
                    className="input mb-3"
                    value={bic || ''}
                    onChange={e => setBic(e.target.value || null)}
                  />
                  <p className="mb-1 text-description">Recipient Name</p>
                  <input
                    type="text"
                    className="input mb-3"
                    value={recipientName || ''}
                    onChange={e => setRecipientName(e.target.value || null)}
                  />
                  <p className="mb-1 text-description">Recipient Address</p>
                  <input
                    type="text"
                    className="input mb-3"
                    value={recipientAddress || ''}
                    onChange={e => setRecipientAddress(e.target.value || null)}
                  />
                  <p className="mb-1 text-description">Recipient Zip Code</p>
                  <input
                    type="text"
                    className="input mb-3"
                    value={recipientZipCode || ''}
                    onChange={e => setRecipientZipCode(e.target.value || null)}
                  />
                  <p className="mb-1 text-description">Recipient City</p>
                  <input
                    type="text"
                    className="input mb-3"
                    value={recipientCity || ''}
                    onChange={e => setRecipientCity(e.target.value || null)}
                  />
                  <p className="mb-1 text-description">Recipient Country</p>
                  <input
                    type="text"
                    className="input mb-3"
                    value={recipientCountry || ''}
                    onChange={e => setRecipientCountry(e.target.value || null)}
                  />
                  <p className="mb-1 text-description">Code</p>
                  <input
                    type="text"
                    className="input mb-3"
                    value={code || ''}
                    onChange={e => setCode(e.target.value || null)}
                  />
                </>
              )}
            </div>

            <button
              disabled={!name.trim()}
              onClick={handleSubmit}
              className="button"
            >
              Save Changes
            </button>
          </div>
        </>
      ) : (
        <div></div>
      )}
    </DefaultModal>
  )
}

export default EditBankAccountModal
